import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Form, Row, Col, Alert } from 'antd'
import { useQuery } from '@apollo/client'
import { useAbility } from '@casl/react'

import QUEUE_SYSTEM_STATUS from 'apollo/queries/QUEUE_SYSTEM_STATUS'
import UserSelect from 'components/UserSelect'
import { Radio, Checkbox, Wysiwyg } from 'ui/styled'
import { Label } from 'pages/Ticket/styled'
import useUserStore from 'stores/user'
import { PermissionsContext, ACTIONS, SUBJECTS } from 'context/Permissions'

import { AssigneeItem, StyledRadio } from './styled'

const { Item } = Form
const { Group } = Radio

const ManageAssigneeItem = ({
  setFieldValue,
  isPersonSelected,
  setPersonSelected,
  assignmentNotesField = 'assignment_notes',
  isTicketCreation = false
}) => {
  const ability = useAbility(PermissionsContext)

  const userId = useUserStore(s => s.userId)
  const { data } = useQuery(QUEUE_SYSTEM_STATUS, {
    skip: ability.cannot(ACTIONS.READ, SUBJECTS.INTAKE_QUEUE)
  })

  const [isPersonDisabled, setPersonDisabled] = useState(!isPersonSelected)
  const [isSelfAssign, setIsSelfAssign] = useState(false)

  const onRadioChange = ({ target: { value } }) => {
    setFieldValue('assigned_to_id', null)
    setFieldValue('assigned_to_me', false)
    setPersonSelected?.(value)
    setPersonDisabled(!value)
  }

  const onChangeAssignee = v => {
    if (userId === v) {
      setFieldValue('assigned_to_me', true)
      setIsSelfAssign(true)
    } else {
      setFieldValue('assigned_to_me', false)
      setIsSelfAssign(false)
    }
  }

  const onChangeSelfAssign = e => {
    setIsSelfAssign(e?.target?.checked)
    if (e?.target?.checked === false) {
      setFieldValue('assigned_to_id', null)
    } else {
      setFieldValue('assigned_to_id', userId)
    }
  }

  const isQueueOff = data?.queue_system_live_status === false
  const requireAssignmentNotes = !isTicketCreation || !isSelfAssign

  return (
    <AssigneeItem>
      <Row>
        <Col span={4}>
          <Group value={isPersonSelected} onChange={onRadioChange}>
            <StyledRadio data-test="assignPersonRadioBtn" value>
              Person
            </StyledRadio>
            <StyledRadio data-test="assignQueueRadioBtn" value={false}>
              Queue
            </StyledRadio>
          </Group>
        </Col>
        <Col span={8} offset={1}>
          <Item name="assigned_to_id" style={{ margin: 0 }}>
            <UserSelect
              placeholder="Person Name"
              showAnyText="Reassign To First Available Navigator"
              data-test="createTicketUserSelect"
              showActiveOnly
              allowClear
              staffOnly
              disabled={isPersonDisabled}
              onChange={onChangeAssignee}
            />
          </Item>
        </Col>
        <Col span={8} offset={1}>
          <Item
            name="assigned_to_me"
            style={{ margin: 0 }}
            valuePropName="checked"
          >
            <Checkbox
              data-test="assignToMeCheckbox"
              disabled={isPersonDisabled}
              onChange={onChangeSelfAssign}
            >
              Assign to Me
            </Checkbox>
          </Item>
        </Col>
      </Row>
      {isQueueOff && !isPersonSelected && (
        <Alert
          type="warning"
          showIcon
          title="The Queue System is currently paused and ticket will be added to queue after the queue system is turned on."
          style={{ marginBottom: 10 }}
        />
      )}
      {isPersonSelected && (
        <Row>
          <Col span={24}>
            <Label required={requireAssignmentNotes}>Assignment Note</Label>
            <Item name={assignmentNotesField} style={{ margin: 0 }}>
              <Wysiwyg
                placeholder="Please enter assignment notes"
                data-test="reassignmentNotesTextArea"
              />
            </Item>
          </Col>
        </Row>
      )}
    </AssigneeItem>
  )
}

ManageAssigneeItem.propTypes = {
  setFieldValue: PropTypes.func.isRequired,
  isPersonSelected: PropTypes.bool.isRequired,
  setPersonSelected: PropTypes.bool.isRequired,
  assignmentNotesField: PropTypes.string,
  isTicketCreation: PropTypes.bool
}

export default ManageAssigneeItem
